// Sprint 2026-Feb-11g — Email Configuration Page (Admin only).
//
// Path: Administration → System Configuration → Email Configuration.
//
// Two cards: "SMTP" (transport creds) + "Estimate Email Settings"
// (per-module send + reminder + templates + CC list). All values are
// admin-editable and NEVER exposed via any list API — this is the ONLY
// UI that touches `/api/email-config`.
//
// Security guarantees enforced UI-side:
//   • Password field is a masked <input type="password"> with the
//     placeholder "••••••••" — the raw ciphertext is never rendered.
//   • Submitting the form with password = "" or "••••••••" leaves the
//     stored password untouched (see backend PUT handler).
//   • "Send Test Email" button hits /email-config/test with the current
//     admin's email as the recipient (spec §2 — Test Email button).

import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Mail, Server, Bell, Send, Save, RotateCw, ShieldAlert } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundary";

// Sprint 2026-Feb-11i-hotfix — safely stringify any error we hand to
// `toast.error()`. Sonner tries to render its first arg as a React
// child, so passing the raw Pydantic 422 `detail` array (as this page
// used to) triggers "Objects are not valid as a React child" and
// unmounts the entire app. `formatApiErrorDetail` covers strings,
// arrays and objects — this thin wrapper adds the network-error path.
function toastFromError(err, fallback) {
  const detail = err?.response?.data?.detail ?? err?.response?.data;
  const msg = detail != null ? formatApiErrorDetail(detail) : (err?.message || fallback);
  toast.error(msg || fallback);
}

const ENC_OPTIONS = ["TLS", "SSL", "NONE"];

// Blank cfg used until the API responds.
const _blank = {
  provider: "smtp",
  smtp_host: "smtp.titan.email",
  smtp_port: 587,
  encryption: "TLS",
  username: "",
  password_mask: "",
  from_name: "",
  from_email: "",
  reply_to: "",
  enabled: false,
  m365: {
    tenant_id: "",
    client_id: "",
    client_secret_mask: "",
    has_client_secret: false,
    sender_name: "",
    sender_email: "",
    reply_to: "",
  },
  estimate: {
    enable_auto_send: false,
    enable_reminder: false,
    reminder_interval_hours: 24,
    max_reminders: 7,
    cc_list: [],
    attach_pdf: true,
    include_quantity_table_in_body: true,
    subject_template: "",
    body_template: "",
    reminder_subject_template: "",
    reminder_body_template: "",
  },
  voucher: {
    enable_auto_send: false,
    cc_list: [],
    attach_pdf: true,
    subject_template: "",
    body_template: "",
  },
  digest: {
    enabled: false,
    recipients: [],
    send_time_ist: "11:30",
  },
  oem: {
    enabled: false,
    cc_list: [],
    send_times_ist: ["10:00", "18:00"],
    subject_template: "",
    body_template: "",
    tagged_subject_template: "",
    tagged_body_template: "",
  },
};

export default function EmailConfigurationPage() {
  // ErrorBoundary wraps the real page so any future render exception
  // shows a graceful inline fallback instead of a blank screen.
  return (
    <ErrorBoundary label="Email Configuration">
      <EmailConfigurationPageInner />
    </ErrorBoundary>
  );
}

function EmailConfigurationPageInner() {
  const [cfg, setCfg] = useState(_blank);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState("");           // ONLY plaintext ever held locally
  const [m365Secret, setM365Secret] = useState("");  // write-only, cleared after save
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testingM365, setTestingM365] = useState(false);
  const [ccInput, setCcInput] = useState("");
  const [vouCcInput, setVouCcInput] = useState("");
  const [digRecInput, setDigRecInput] = useState("");
  const [oemCcInput, setOemCcInput] = useState("");
  const [oemSweepBusy, setOemSweepBusy] = useState(false);
  const [oemSweepResult, setOemSweepResult] = useState(null);
  const [digBusy, setDigBusy] = useState(false);
  const [digPreview, setDigPreview] = useState(null);
  const [sweepBusy, setSweepBusy] = useState(false);
  const [sweepResult, setSweepResult] = useState(null);

  async function runDigest(dryRun) {
    setDigBusy(true);
    try {
      const r = await api.post("/email-config/digest/send-now",
        { dry_run: dryRun }, { timeout: 180000 });
      if (dryRun) {
        setDigPreview(r.data);
        toast.success(`Preview built for ${r.data?.data?.date} — ${r.data?.data?.project_count} project(s)`);
      } else if (r.data.success) {
        toast.success(r.data.message);
      } else {
        toast.error(r.data.message || "Digest send failed");
      }
    } catch (e) {
      toastFromError(e, "Digest request failed");
    } finally { setDigBusy(false); }
  }

  async function runSweep(dryRun) {
    setSweepBusy(true);
    try {
      const r = await api.post("/email-config/estimate/reminder-sweep",
        { dry_run: dryRun }, { timeout: 180000 });
      setSweepResult(r.data);
      if (r.data.halted) toast.warning(r.data.halted);
      else if (dryRun) toast.success(`${r.data.due} reminder(s) currently due`);
      else toast.success(`${r.data.sent} reminder(s) sent · ${r.data.failed} failed · ${r.data.skipped} skipped`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reminder sweep failed");
    } finally { setSweepBusy(false); }
  }

  useEffect(() => { reload(); }, []);
  async function reload() {
    setLoading(true);
    try {
      const { data } = await api.get("/email-config");
      setCfg({ ..._blank, ...data,
               m365: { ..._blank.m365, ...(data.m365 || {}) },
               estimate: { ..._blank.estimate, ...(data.estimate || {}) },
               voucher: { ..._blank.voucher, ...(data.voucher || {}) },
               digest: { ..._blank.digest, ...(data.digest || {}) },
               oem: { ..._blank.oem, ...(data.oem || {}) } });
      setCcInput((data.estimate?.cc_list || []).join(", "));
      setVouCcInput((data.voucher?.cc_list || []).join(", "));
      setDigRecInput((data.digest?.recipients || []).join(", "));
      setOemCcInput((data.oem?.cc_list || []).join(", "));
      setPwd("");
      setM365Secret("");
    } catch (e) {
      toastFromError(e, "Failed to load Email Configuration");
    } finally {
      setLoading(false);
    }
  }

  function set(field, value) { setCfg(prev => ({ ...prev, [field]: value })); }
  function setEst(field, value) {
    setCfg(prev => ({ ...prev, estimate: { ...(prev.estimate || {}), [field]: value } }));
  }
  function setVou(field, value) {
    setCfg(prev => ({ ...prev, voucher: { ...(prev.voucher || {}), [field]: value } }));
  }
  function setDig(field, value) {
    setCfg(prev => ({ ...prev, digest: { ...(prev.digest || {}), [field]: value } }));
  }
  function setOem(field, value) {
    setCfg(prev => ({ ...prev, oem: { ...(prev.oem || {}), [field]: value } }));
  }
  function setM365(field, value) {
    setCfg(prev => ({ ...prev, m365: { ...(prev.m365 || {}), [field]: value } }));
  }

  async function runOemSweep(dryRun) {
    setOemSweepBusy(true);
    try {
      const r = await api.post("/oem-complaints/sweep", { dry_run: dryRun }, { timeout: 180000 });
      setOemSweepResult(r.data);
      if (r.data.halted) toast.warning(r.data.halted);
      else if (dryRun) toast.success(`${r.data.due} complaint mail(s) currently due`);
      else toast.success(`${r.data.sent} sent · ${r.data.failed} failed · ${r.data.skipped} skipped`);
    } catch (e) {
      toastFromError(e, "OEM sweep failed");
    } finally { setOemSweepBusy(false); }
  }

  async function save() {
    setSaving(true);
    try {
      // CC list: keep only well-formed emails; drop stray whitespace /
      // dangling commas so Pydantic's EmailStr validator can't 422.
      const cc_list = ccInput
        .split(/[,\n]/g)
        .map(s => s.trim())
        .filter(s => s && s.includes("@"));

      // Sprint 2026-Feb-11i-hotfix — strip empty strings from Optional
      // EmailStr fields on the backend (`from_email`, `reply_to`).
      // Pydantic v2 rejects "" for EmailStr, and the resulting 422
      // returned an array-shaped `detail` that crashed the toast layer.
      const payload = {
        provider: cfg.provider || "smtp",
        smtp_host: cfg.smtp_host,
        smtp_port: Number(cfg.smtp_port) || 587,
        encryption: cfg.encryption,
        username: cfg.username || "",
        from_name: cfg.from_name || "",
        enabled: !!cfg.enabled,
        estimate: {
          enable_auto_send:          !!cfg.estimate.enable_auto_send,
          enable_reminder:           !!cfg.estimate.enable_reminder,
          reminder_interval_hours:   Number(cfg.estimate.reminder_interval_hours) || 24,
          max_reminders:             Number(cfg.estimate.max_reminders) || 7,
          cc_list,
          attach_pdf:                !!cfg.estimate.attach_pdf,
          include_quantity_table_in_body: !!cfg.estimate.include_quantity_table_in_body,
          subject_template:          cfg.estimate.subject_template || "",
          body_template:             cfg.estimate.body_template || "",
          reminder_subject_template: cfg.estimate.reminder_subject_template || "",
          reminder_body_template:    cfg.estimate.reminder_body_template || "",
        },
        voucher: {
          enable_auto_send: !!cfg.voucher.enable_auto_send,
          attach_pdf:       !!cfg.voucher.attach_pdf,
          cc_list: vouCcInput
            .split(/[,\n]/g)
            .map(s => s.trim())
            .filter(s => s && s.includes("@")),
          subject_template: cfg.voucher.subject_template || "",
          body_template:    cfg.voucher.body_template || "",
        },
        digest: {
          enabled: !!cfg.digest.enabled,
          send_time_ist: cfg.digest.send_time_ist || "11:30",
          recipients: digRecInput
            .split(/[,\n]/g)
            .map(s => s.trim())
            .filter(s => s && s.includes("@")),
        },
        oem: {
          enabled: !!cfg.oem.enabled,
          cc_list: oemCcInput
            .split(/[,\n]/g)
            .map(s => s.trim())
            .filter(s => s && s.includes("@")),
          send_times_ist: [...new Set((cfg.oem.send_times_ist || [])
            .filter(t => /^([01]\d|2[0-3]):[0-5]\d$/.test(t)))].sort(),
          subject_template:        cfg.oem.subject_template || "",
          body_template:           cfg.oem.body_template || "",
          tagged_subject_template: cfg.oem.tagged_subject_template || "",
          tagged_body_template:    cfg.oem.tagged_body_template || "",
        },
      };
      const fromEmail = (cfg.from_email || "").trim();
      if (fromEmail) payload.from_email = fromEmail;
      const replyTo = (cfg.reply_to || "").trim();
      if (replyTo) payload.reply_to = replyTo;

      // Microsoft 365 sub-block — secret is write-only: sent ONLY when
      // the admin typed a fresh value (never the mask, never echoed back).
      const m365 = {
        tenant_id: (cfg.m365?.tenant_id || "").trim(),
        client_id: (cfg.m365?.client_id || "").trim(),
        sender_name: cfg.m365?.sender_name || "",
      };
      const m365Sender = (cfg.m365?.sender_email || "").trim();
      if (m365Sender) m365.sender_email = m365Sender;
      const m365ReplyTo = (cfg.m365?.reply_to || "").trim();
      if (m365ReplyTo) m365.reply_to = m365ReplyTo;
      if (m365Secret && m365Secret !== "••••••••") m365.client_secret_plain = m365Secret;
      payload.m365 = m365;

      // Only send password when user typed something new.
      if (pwd && pwd !== "••••••••") payload.password_plain = pwd;
      await api.put("/email-config", payload);
      toast.success("Email configuration saved");
      await reload();
    } catch (e) {
      toastFromError(e, "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function testEmail(provider) {
    if (!testTo || !testTo.includes("@")) {
      toast.error("Enter a valid recipient email for the test.");
      return;
    }
    const setBusy = provider === "m365" ? setTestingM365 : setTesting;
    setBusy(true);
    try {
      // /test always returns HTTP 200 with {success, message, category?}.
      // We branch on `success` — no need for a try/catch on axios errors
      // in the normal flow. Network-level failure still lands in catch.
      const body = { to: testTo };
      if (provider) body.provider = provider;
      const { data } = await api.post("/email-config/test", body);
      if (data?.success) {
        toast.success(data.message || `Test email sent to ${testTo} ✓`);
      } else {
        toast.error(data?.message || "Test send failed");
      }
    } catch (e) {
      // Reserved for genuine network / auth failures. `detail` may be
      // a Pydantic 422 array — use the safe formatter, never a raw obj.
      const raw = e?.response?.data?.message ?? e?.response?.data?.detail;
      toast.error(raw ? formatApiErrorDetail(raw) : (e?.message || "Test send failed"));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return (
    <div className="p-6 text-slate-500 text-sm" data-testid="email-config-loading">
      Loading Email Configuration…
    </div>
  );

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl" data-testid="email-config-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-600" />
            Email Configuration
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            SMTP transport + Estimate email templates &amp; reminders. Reusable for
            other vouchers (Sales Invoice, PO, WO…) in later phases.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={reload} disabled={saving}
                  data-testid="email-config-reload">
            <RotateCw className="w-4 h-4 mr-1" /> Reload
          </Button>
          <Button size="sm" onClick={save} disabled={saving}
                  data-testid="email-config-save"
                  className="bg-orange-600 hover:bg-orange-700 text-white">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {/* ── Encryption-health banner (Sprint 11i-hotfix) ─────────────
          Refuses PUT of a new password when EMAIL_ENC_KEY is missing
          on the backend; also flags legacy plaintext passwords stored
          before the key was configured. Both states halt the module
          for security — every other ERP feature keeps working.       */}
      {cfg.encryption_configured === false && (
        <div className="border border-rose-300 bg-rose-50 p-3 flex items-start gap-3"
             data-testid="banner-encryption-unavailable">
          <ShieldAlert className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider font-bold text-rose-700">
              Secure email encryption not configured
            </div>
            <div className="text-xs text-slate-800 mt-1">
              The Email module is disabled because the server’s
              <code className="mx-1 px-1 bg-white border border-rose-200">EMAIL_ENC_KEY</code>
              is missing or invalid. Please contact the system administrator.
              SMTP passwords cannot be saved and no emails will be sent until this is fixed.
            </div>
          </div>
        </div>
      )}
      {cfg.encryption_configured !== false && cfg.insecure_password_stored && (
        <div className="border border-amber-300 bg-amber-50 p-3 flex items-start gap-3"
             data-testid="banner-insecure-password">
          <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider font-bold text-amber-800">
              Insecure SMTP password detected
            </div>
            <div className="text-xs text-slate-800 mt-1">
              An unencrypted SMTP password was found in the database (from before
              this server’s encryption key was configured). Please re-enter the
              SMTP password below and click <b>Save</b> so it can be stored
              securely. The Email module is halted until you do.
            </div>
          </div>
        </div>
      )}

      {/* ── Active Provider selector (Jun-2026 — M365 Graph) ─────── */}
      <Card data-testid="provider-card">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 border-b pb-3">
            <Send className="w-4 h-4 text-slate-600" />
            <h2 className="font-bold text-slate-800">Active Email Provider</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid="provider-smtp-btn"
                    onClick={() => set("provider", "smtp")}
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border rounded ${
                      (cfg.provider || "smtp") === "smtp"
                        ? "bg-slate-800 text-white border-slate-800"
                        : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
              SMTP (Titan / classic)
            </button>
            <button type="button" data-testid="provider-m365-btn"
                    onClick={() => set("provider", "m365")}
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border rounded ${
                      cfg.provider === "m365"
                        ? "bg-[#0078D4] text-white border-[#0078D4]"
                        : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
              Microsoft 365 (Graph API)
            </button>
          </div>
          <p className="text-[11px] text-slate-500">
            All PrimeWork emails (Estimates, OEM reminders, Vouchers, DPR, Digest, Test)
            are sent through the selected provider. Selecting <b>Microsoft 365</b> requires
            complete M365 settings below — save is rejected otherwise, and the current
            provider stays active. Send a successful Test Email from the M365 card before switching.
          </p>
        </CardContent>
      </Card>

      {/* ── SMTP Transport card ─────────────────────────────────── */}
      <Card data-testid="smtp-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-3 border-b pb-3">
            <div className="flex items-center gap-2">
              <Server className="w-4 h-4 text-slate-600" />
              <h2 className="font-bold text-slate-800">SMTP Transport</h2>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Label htmlFor="email-enabled" className="text-slate-600">Enable Email</Label>
              <Switch id="email-enabled" checked={!!cfg.enabled}
                      onCheckedChange={v => set("enabled", v)}
                      data-testid="email-enabled-switch" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="SMTP Host">
              <Input value={cfg.smtp_host || ""} onChange={e => set("smtp_host", e.target.value)}
                     placeholder="smtp.titan.email" data-testid="smtp-host" />
            </Field>
            <Field label="SMTP Port">
              <Input type="number" value={cfg.smtp_port || 587}
                     onChange={e => set("smtp_port", e.target.value)}
                     data-testid="smtp-port" />
            </Field>
            <Field label="Encryption">
              <select value={cfg.encryption || "TLS"}
                      onChange={e => set("encryption", e.target.value)}
                      data-testid="smtp-encryption"
                      className="w-full border rounded px-2 py-1.5 text-sm bg-white">
                {ENC_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="SMTP Username">
              <Input value={cfg.username || ""} onChange={e => set("username", e.target.value)}
                     placeholder="you@titan.example.com" data-testid="smtp-username"
                     autoComplete="off" />
            </Field>
            <Field label="SMTP Password">
              <Input type="password" value={pwd}
                     onChange={e => setPwd(e.target.value)}
                     placeholder={cfg.password_mask || "Enter password"}
                     data-testid="smtp-password"
                     autoComplete="new-password" />
              <p className="text-[10px] text-slate-400 mt-1">
                {cfg.password_mask
                  ? "A password is on file. Leave blank to keep it unchanged."
                  : "No password on file yet."}
              </p>
            </Field>
            <Field label="Sender Name">
              <Input value={cfg.from_name || ""} onChange={e => set("from_name", e.target.value)}
                     placeholder="Radiants Civiletric Pvt Ltd" data-testid="smtp-from-name" />
            </Field>
            <Field label="Sender Email">
              <Input type="email" value={cfg.from_email || ""}
                     onChange={e => set("from_email", e.target.value)}
                     placeholder="noreply@yourcompany.com" data-testid="smtp-from-email" />
            </Field>
            <Field label="Reply-To Email">
              <Input type="email" value={cfg.reply_to || ""}
                     onChange={e => set("reply_to", e.target.value)}
                     placeholder="billing@yourcompany.com" data-testid="smtp-reply-to" />
            </Field>
          </div>

          <div className="pt-4 border-t flex items-end gap-3">
            <div className="flex-1 max-w-sm">
              <Label className="text-xs text-slate-500">Test recipient</Label>
              <Input type="email" value={testTo} onChange={e => setTestTo(e.target.value)}
                     placeholder="your@email.com" data-testid="smtp-test-to" />
            </div>
            <Button onClick={() => testEmail("smtp")} disabled={testing}
                    data-testid="smtp-test-btn"
                    className="bg-slate-800 hover:bg-slate-900 text-white">
              <Send className="w-4 h-4 mr-1" />
              {testing ? "Sending…" : "Send Test Email (SMTP)"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Microsoft 365 (Graph API) card ──────────────────────── */}
      <Card data-testid="m365-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-3 border-b pb-3">
            <div className="flex items-center gap-2">
              <Server className="w-4 h-4 text-[#0078D4]" />
              <h2 className="font-bold text-slate-800">Microsoft 365 (Graph API)</h2>
            </div>
            {cfg.provider === "m365" && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 bg-[#0078D4] text-white"
                    data-testid="m365-active-badge">Active Provider</span>
            )}
          </div>
          <p className="text-[11px] text-slate-500 -mt-1">
            OAuth 2.0 client credentials (Entra ID App Registration with the <b>Application</b> permission
            <b> Mail.Send</b> + admin consent). The Client Secret is encrypted at rest and never shown again after saving.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Tenant ID (Directory ID)">
              <Input value={cfg.m365?.tenant_id || ""}
                     onChange={e => setM365("tenant_id", e.target.value)}
                     placeholder="00000000-0000-0000-0000-000000000000"
                     data-testid="m365-tenant-id" autoComplete="off" />
            </Field>
            <Field label="Client ID (Application ID)">
              <Input value={cfg.m365?.client_id || ""}
                     onChange={e => setM365("client_id", e.target.value)}
                     placeholder="00000000-0000-0000-0000-000000000000"
                     data-testid="m365-client-id" autoComplete="off" />
            </Field>
            <Field label="Client Secret (Value, not Secret ID)">
              <Input type="password" value={m365Secret}
                     onChange={e => setM365Secret(e.target.value)}
                     placeholder={cfg.m365?.client_secret_mask || "Enter client secret"}
                     data-testid="m365-client-secret"
                     autoComplete="new-password" />
              <p className="text-[10px] text-slate-400 mt-1">
                {cfg.m365?.has_client_secret
                  ? "A client secret is on file. Leave blank to keep it unchanged."
                  : "No client secret on file yet."}
              </p>
            </Field>
            <Field label="Sender Email (licensed M365 mailbox)">
              <Input type="email" value={cfg.m365?.sender_email || ""}
                     onChange={e => setM365("sender_email", e.target.value)}
                     placeholder="noreply@yourcompany.com"
                     data-testid="m365-sender-email" />
            </Field>
            <Field label="Sender Name">
              <Input value={cfg.m365?.sender_name || ""}
                     onChange={e => setM365("sender_name", e.target.value)}
                     placeholder="Radiants Civiletric Pvt Ltd"
                     data-testid="m365-sender-name" />
            </Field>
            <Field label="Reply-To Email">
              <Input type="email" value={cfg.m365?.reply_to || ""}
                     onChange={e => setM365("reply_to", e.target.value)}
                     placeholder="billing@yourcompany.com"
                     data-testid="m365-reply-to" />
            </Field>
          </div>

          <div className="pt-4 border-t flex items-end gap-3">
            <div className="flex-1 max-w-sm">
              <Label className="text-xs text-slate-500">Test recipient</Label>
              <Input type="email" value={testTo} onChange={e => setTestTo(e.target.value)}
                     placeholder="your@email.com" data-testid="m365-test-to" />
            </div>
            <Button onClick={() => testEmail("m365")} disabled={testingM365}
                    data-testid="m365-test-btn"
                    className="bg-[#0078D4] hover:bg-[#106EBE] text-white">
              <Send className="w-4 h-4 mr-1" />
              {testingM365 ? "Sending…" : "Send Test Email (Microsoft 365)"}
            </Button>
          </div>
          <p className="text-[10px] text-slate-400">
            Save the M365 settings first, then test. Testing here uses Microsoft 365 even
            while SMTP is still the active provider — switch only after a successful test.
          </p>
        </CardContent>
      </Card>

      {/* ── Estimate Email Settings card ────────────────────────── */}
      <Card data-testid="estimate-email-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2 border-b pb-3">
            <Bell className="w-4 h-4 text-slate-600" />
            <h2 className="font-bold text-slate-800">Estimate Email Settings</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SwitchRow label="Auto-Send Email after Save"
                       checked={!!cfg.estimate.enable_auto_send}
                       onChange={v => setEst("enable_auto_send", v)}
                       testid="est-auto-send" />
            <SwitchRow label="Enable Automatic Reminder"
                       checked={!!cfg.estimate.enable_reminder}
                       onChange={v => setEst("enable_reminder", v)}
                       testid="est-reminder-enabled" />
            <SwitchRow label="Attach Estimate PDF"
                       checked={!!cfg.estimate.attach_pdf}
                       onChange={v => setEst("attach_pdf", v)}
                       testid="est-attach-pdf" />
            <SwitchRow label="Include Quantity Table in Email Body"
                       checked={!!cfg.estimate.include_quantity_table_in_body}
                       onChange={v => setEst("include_quantity_table_in_body", v)}
                       testid="est-include-quantity-table" />
            <Field label="Reminder Interval (hours)">
              <Input type="number" min={1} max={720}
                     value={cfg.estimate.reminder_interval_hours || 24}
                     onChange={e => setEst("reminder_interval_hours", e.target.value)}
                     data-testid="est-reminder-interval" />
            </Field>
            <Field label="Maximum Reminders">
              <Input type="number" min={1} max={50}
                     value={cfg.estimate.max_reminders || 7}
                     onChange={e => setEst("max_reminders", e.target.value)}
                     data-testid="est-max-reminders" />
              <p className="text-[10px] text-slate-400 mt-1">
                Default 7. Editable to 10 or any value you prefer.
              </p>
            </Field>
            <Field label="Default CC List (comma separated)">
              <Input value={ccInput} onChange={e => setCcInput(e.target.value)}
                     placeholder="cc1@example.com, cc2@example.com"
                     data-testid="est-cc-list" />
            </Field>
          </div>

          {/* Phase D — reminder scheduler runs every 5 min; manual trigger for verification */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" disabled={sweepBusy}
                    onClick={() => runSweep(true)} data-testid="est-reminder-sweep-preview">
              <RotateCw className="w-3.5 h-3.5 mr-1" /> Check Due Reminders
            </Button>
            <Button type="button" size="sm" disabled={sweepBusy}
                    onClick={() => runSweep(false)} data-testid="est-reminder-sweep-run">
              <Send className="w-3.5 h-3.5 mr-1" /> Send Due Reminders Now
            </Button>
            <span className="text-[10px] text-slate-400">
              Reminders are sent automatically every 5 minutes while the switch above is ON. Save settings first.
            </span>
          </div>
          {sweepResult && (
            <div className="text-[11px] text-slate-600 border border-slate-200 bg-slate-50 px-2.5 py-1.5"
                 data-testid="est-reminder-sweep-result">
              {sweepResult.halted
                ? `Halted: ${sweepResult.halted}`
                : `${sweepResult.dry_run ? "Preview" : "Run"} — eligible ${sweepResult.eligible} · due ${sweepResult.due} · sent ${sweepResult.sent} · failed ${sweepResult.failed} · skipped ${sweepResult.skipped}`}
              {(sweepResult.details || []).slice(0, 6).map((d, i) => (
                <div key={i} className="text-[10px] text-slate-500">
                  • {d.estimate_number}: {d.action}{d.reminder_number ? ` (reminder #${d.reminder_number})` : ""}{d.reason ? ` — ${d.reason}` : ""}
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
            <Field label="Initial Email — Subject">
              <Input value={cfg.estimate.subject_template || ""}
                     onChange={e => setEst("subject_template", e.target.value)}
                     data-testid="est-subject-tpl" />
            </Field>
            <Field label="Reminder — Subject">
              <Input value={cfg.estimate.reminder_subject_template || ""}
                     onChange={e => setEst("reminder_subject_template", e.target.value)}
                     data-testid="est-reminder-subject-tpl" />
            </Field>
            <Field label="Initial Email — Body (HTML)">
              <Textarea rows={7} value={cfg.estimate.body_template || ""}
                        onChange={e => setEst("body_template", e.target.value)}
                        data-testid="est-body-tpl" />
            </Field>
            <Field label="Reminder — Body (HTML)">
              <Textarea rows={7} value={cfg.estimate.reminder_body_template || ""}
                        onChange={e => setEst("reminder_body_template", e.target.value)}
                        data-testid="est-reminder-body-tpl" />
            </Field>
          </div>

          <div className="bg-amber-50 border border-amber-200 text-xs text-amber-800 p-3 rounded">
            <b>Placeholders</b> — use these anywhere in Subject or Body:{" "}
            <code className="text-[10px]">{`{{client_name}}, {{client_poc_name}}, {{estimate_number}}, {{estimate_date}}, {{estimate_amount}}, {{project_name}}, {{company_name}}, {{branch_name}}, {{user_name}}, {{reminder_count}}, {{pending_days}}, {{ref_no}}, {{note}}, {{shipping_address_label}}`}</code>
            <div className="mt-1 text-[10px] text-amber-700">
              <b>{`{{pending_days}}`}</b> — recomputed on every send. Great for reminder subject lines like{" "}
              <code>Reminder (Day {`{{pending_days}}`}): Approval Pending for Estimate {`{{estimate_number}}`}</code>.
            </div>
            <div className="mt-1 text-[10px] text-amber-700">
              <b>{`{{ref_no}}`}</b> — the voucher&apos;s Ref No header field.{" "}
              <b>{`{{note}}`}</b> — the voucher&apos;s Notes field (also accepts <code>{`{{notes}}`}</code>). Both render as empty when the field is blank.
            </div>
            <div className="mt-1 text-[10px] text-amber-700">
              <b>{`{{shipping_address_label}}`}</b> — the label of the shipping address chosen on this
              estimate (e.g. <code>Warehouse A</code>, <code>HQ Delhi</code>, <code>Same as Billing</code>).
              Useful in the subject line so recipients see at a glance <i>which</i> site an estimate is for:
              <br/><code>Estimate {`{{estimate_number}}`} — Ship to {`{{shipping_address_label}}`}</code>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Issue / Return Voucher Email Settings (Jul-2026) ──────── */}
      <Card data-testid="voucher-email-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2 border-b pb-3">
            <Send className="w-4 h-4 text-slate-600" />
            <h2 className="font-bold text-slate-800">Issue / Return Voucher Email Settings</h2>
          </div>
          <p className="text-xs text-slate-500 -mt-1">
            When an Issue or Return Voucher is <b>finally approved</b>, an email is sent
            automatically to the voucher&apos;s party — <b>Member</b> (Member library email),
            <b> Vendor</b> (Vendor library email) or <b>Client</b> (Client library email).
            The Default CC list below is CC&apos;d on every mail, and becomes the
            <b> To</b>-recipient whenever the party has no email on record.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SwitchRow label="Auto-Send Email on Final Approval"
                       checked={!!cfg.voucher.enable_auto_send}
                       onChange={v => setVou("enable_auto_send", v)}
                       testid="vou-auto-send" />
            <SwitchRow label="Attach Voucher PDF (no-amount version)"
                       checked={!!cfg.voucher.attach_pdf}
                       onChange={v => setVou("attach_pdf", v)}
                       testid="vou-attach-pdf" />
            <Field label="Default CC List (comma separated)">
              <Input value={vouCcInput} onChange={e => setVouCcInput(e.target.value)}
                     placeholder="stores@example.com, accounts@example.com"
                     data-testid="vou-cc-list" />
              <p className="text-[10px] text-slate-400 mt-1">
                CC&apos;d on every voucher email · fallback To-recipient when the
                Member / Vendor / Client has no email in their library record.
              </p>
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
            <Field label="Subject Template">
              <Input value={cfg.voucher.subject_template || ""}
                     onChange={e => setVou("subject_template", e.target.value)}
                     data-testid="vou-subject-tpl" />
            </Field>
            <Field label="Body Template (HTML)">
              <Textarea rows={7} value={cfg.voucher.body_template || ""}
                        onChange={e => setVou("body_template", e.target.value)}
                        data-testid="vou-body-tpl" />
            </Field>
          </div>

          <div className="bg-amber-50 border border-amber-200 text-xs text-amber-800 p-3 rounded">
            <b>Placeholders</b> — use these anywhere in Subject or Body:{" "}
            <code className="text-[10px]">{`{{voucher_type}}, {{voucher_number}}, {{voucher_date}}, {{party_name}}, {{party_type}}, {{party_code}}, {{party_id}}, {{branch_name}}, {{project_name}}, {{company_name}}, {{items_table}}`}</code>
            <div className="mt-1 text-[10px] text-amber-700">
              <b>{`{{party_name}}`}</b> / <b>{`{{party_code}}`}</b> — the Member / Vendor / Client name and
              code from the respective library (code renders in parentheses, empty when none).{" "}
              <b>{`{{items_table}}`}</b> — quantity-only line-item table (no amounts).
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── OEM Complaint Email Settings (Jun-2026) ───────────────── */}
      <Card data-testid="oem-email-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2 border-b pb-3">
            <Bell className="w-4 h-4 text-slate-600" />
            <h2 className="font-bold text-slate-800">OEM Complaint Email Settings</h2>
          </div>
          <p className="text-xs text-slate-500 -mt-1">
            Vendor chase mails for client-site complaints. <b>To</b> = every email on the
            vendor&apos;s master record (main + additional). <b>CC</b> = the default list below
            + the store&apos;s shipping-address email + the Client POC (AFM)&apos;s email
            (each skipped when not on file).
            Mails fire <b>immediately on entry</b>, then at <b>the daily times you set below</b>
            {" "}until the complaint is marked Resolved or Client Scope. Once a <b>Vendor Complaint Register Number</b>
            {" "}is tagged, the &quot;After Tagging&quot; templates take over.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SwitchRow label="Enable OEM Complaint Mails"
                       checked={!!cfg.oem.enabled}
                       onChange={v => setOem("enabled", v)}
                       testid="oem-enabled" />
            <Field label="Default CC List (comma separated)">
              <Input value={oemCcInput} onChange={e => setOemCcInput(e.target.value)}
                     placeholder="helpdesk@example.com, supervisor@example.com"
                     data-testid="oem-cc-list" />
              <p className="text-[10px] text-slate-400 mt-1">
                CC&apos;d on every complaint mail, together with the store&apos;s shipping-address email.
              </p>
            </Field>
          </div>

          <Field label={`Daily Reminder Times (IST) — ${(cfg.oem.send_times_ist || []).length} mail(s) per day`}>
            <div className="flex flex-wrap items-center gap-2" data-testid="oem-times-editor">
              {(cfg.oem.send_times_ist || []).map((t, i) => (
                <span key={i} className="flex items-center gap-1 border border-slate-200 rounded px-1.5 py-1 bg-slate-50">
                  <Input type="time" value={t}
                         onChange={e => setOem("send_times_ist",
                           (cfg.oem.send_times_ist || []).map((x, j) => j === i ? e.target.value : x))}
                         className="w-28 h-8 text-xs" data-testid={`oem-time-${i}`} />
                  <button type="button" data-testid={`oem-time-remove-${i}`}
                          onClick={() => setOem("send_times_ist",
                            (cfg.oem.send_times_ist || []).filter((_, j) => j !== i))}
                          className="text-rose-500 hover:text-rose-700 font-bold px-1">×</button>
                </span>
              ))}
              <Button type="button" variant="outline" size="sm" data-testid="oem-time-add"
                      onClick={() => setOem("send_times_ist", [...(cfg.oem.send_times_ist || []), "12:00"])}>
                + Add Time
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">
              One reminder fires per slot each day — add or remove slots to control how many mails go out daily.
              Duplicates are merged on save. Removing all slots stops daily reminders (only the immediate
              on-entry mail is sent).
            </p>
          </Field>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" disabled={oemSweepBusy}
                    onClick={() => runOemSweep(true)} data-testid="oem-sweep-preview">
              <RotateCw className="w-3.5 h-3.5 mr-1" /> Check Due Mails
            </Button>
            <Button type="button" size="sm" disabled={oemSweepBusy}
                    onClick={() => runOemSweep(false)} data-testid="oem-sweep-run">
              <Send className="w-3.5 h-3.5 mr-1" /> Send Due Mails Now
            </Button>
            <span className="text-[10px] text-slate-400">
              The scheduler checks every 5 minutes against your configured IST slots. Save settings first.
            </span>
          </div>
          {oemSweepResult && (
            <div className="text-[11px] text-slate-600 border border-slate-200 bg-slate-50 px-2.5 py-1.5"
                 data-testid="oem-sweep-result">
              {oemSweepResult.halted
                ? `Halted: ${oemSweepResult.halted}`
                : `${oemSweepResult.dry_run ? "Preview" : "Run"} — open ${oemSweepResult.open} · due ${oemSweepResult.due} · sent ${oemSweepResult.sent} · failed ${oemSweepResult.failed} · skipped ${oemSweepResult.skipped}`}
              {(oemSweepResult.details || []).slice(0, 6).map((d, i) => (
                <div key={i} className="text-[10px] text-slate-500">
                  • {d.ticket_number}: {d.action}{d.reason ? ` — ${d.reason}` : ""}
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
            <Field label="Before Tagging — Subject">
              <Input value={cfg.oem.subject_template || ""}
                     onChange={e => setOem("subject_template", e.target.value)}
                     data-testid="oem-subject-tpl" />
            </Field>
            <Field label="After Tagging (Vendor Complaint No known) — Subject">
              <Input value={cfg.oem.tagged_subject_template || ""}
                     onChange={e => setOem("tagged_subject_template", e.target.value)}
                     data-testid="oem-tagged-subject-tpl" />
            </Field>
            <Field label="Before Tagging — Body (HTML)">
              <Textarea rows={9} value={cfg.oem.body_template || ""}
                        onChange={e => setOem("body_template", e.target.value)}
                        data-testid="oem-body-tpl" />
            </Field>
            <Field label="After Tagging — Body (HTML)">
              <Textarea rows={9} value={cfg.oem.tagged_body_template || ""}
                        onChange={e => setOem("tagged_body_template", e.target.value)}
                        data-testid="oem-tagged-body-tpl" />
            </Field>
          </div>

          <div className="bg-amber-50 border border-amber-200 text-xs text-amber-800 p-3 rounded">
            <b>Placeholders</b> — use these anywhere in Subject or Body:{" "}
            <code className="text-[10px]">{`{{ticket_number}}, {{product_id}}, {{client_name}}, {{client_code}}, {{store_id}}, {{store_label}}, {{store_address}}, {{category}}, {{sub_category}}, {{issue_type}}, {{issue_title}}, {{afm_name}}, {{afm_phone}}, {{supervisor_name}}, {{supervisor_phone}}, {{vendor_name}}, {{vendor_code}}, {{vendor_complaint_number}}, {{vendor_complaint_date}}, {{created_at}}, {{company_name}}, {{reminder_tag}}, {{reminder_line}}, {{reopen_remark}}, {{reopen_remark_line}}`}</code>
            <div className="mt-1 text-[10px] text-amber-700">
              <b>{`{{reminder_tag}}`}</b> renders as <code>Reminder 1: </code>, <code>Reminder 2: </code>…
              on repeat sends and empty on the first mail — keep it at the start of both Subjects.{" "}
              <b>{`{{reminder_line}}`}</b> is the matching bold line for the Body top.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Daily Ops Digest (Jun-2026) ───────────────────────────── */}
      <Card data-testid="digest-email-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2 border-b pb-3">
            <Send className="w-4 h-4 text-slate-600" />
            <h2 className="font-bold text-slate-800">Daily Ops Digest</h2>
          </div>
          <p className="text-xs text-slate-500 -mt-1">
            A morning email summarising <b>yesterday</b> across all active ops projects:
            <b> Work Done ₹</b> (BOQ progress), <b>Labour</b> (effective headcount + cost),
            <b> DPR status</b> (submitted / missing) and open <b>Blockers</b>, plus portfolio totals.
            Uses the SMTP settings above. Manual sends do not block the scheduled one.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SwitchRow label="Send Digest Automatically Every Day"
                       checked={!!cfg.digest.enabled}
                       onChange={v => setDig("enabled", v)}
                       testid="digest-enabled" />
            <Field label="Send Time (IST, 24h)">
              <Input type="time" value={cfg.digest.send_time_ist || "11:30"}
                     onChange={e => setDig("send_time_ist", e.target.value)}
                     data-testid="digest-send-time" />
            </Field>
            <Field label="Recipients (comma separated)">
              <Input value={digRecInput} onChange={e => setDigRecInput(e.target.value)}
                     placeholder="md@company.com, ops@company.com"
                     data-testid="digest-recipients" />
            </Field>
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" disabled={digBusy}
                    onClick={() => runDigest(true)} data-testid="digest-preview-btn">
              <RotateCw className="w-3.5 h-3.5 mr-1" /> Preview Digest
            </Button>
            <Button type="button" size="sm" disabled={digBusy}
                    onClick={() => runDigest(false)} data-testid="digest-send-now-btn">
              <Send className="w-3.5 h-3.5 mr-1" /> Send Digest Now
            </Button>
            <span className="text-[10px] text-slate-400">
              Save settings first — Send Now emails the saved recipient list immediately.
            </span>
          </div>
          {digPreview?.data && (
            <div className="border border-slate-200 rounded overflow-hidden" data-testid="digest-preview">
              <div className="px-3 py-2 bg-slate-50 text-[11px] text-slate-600 font-bold flex items-center justify-between">
                <span>Preview — {digPreview.data.date} · {digPreview.data.project_count} project(s) ·
                  Work {`₹${Number(digPreview.data.totals.work_done).toLocaleString("en-IN")}`} ·
                  Labour {`₹${Number(digPreview.data.totals.labour_cost).toLocaleString("en-IN")}`} ·
                  DPRs {digPreview.data.totals.dpr_submitted}/{digPreview.data.project_count} ·
                  Blockers {digPreview.data.totals.blockers}</span>
                <button className="text-slate-400 hover:text-slate-700 text-xs" onClick={() => setDigPreview(null)} data-testid="digest-preview-close">✕</button>
              </div>
              <div className="max-h-80 overflow-y-auto p-3 bg-white"
                   dangerouslySetInnerHTML={{ __html: digPreview.html }} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-600 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SwitchRow({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 border rounded px-3 py-2 bg-slate-50">
      <Label className="text-xs text-slate-700 font-semibold">{label}</Label>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
